import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Store, Bell, Mail, MessageSquare, HelpCircle, LogOut, ChevronRight, User, Loader2 } from "lucide-react";
import AppShell from "@/components/AppShell";
import { getSessionBusiness, logoutSession } from "@/lib/api";
import { toast } from "sonner";

export default function Settings() {
  const navigate = useNavigate();
  const [business, setBusiness] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [emailNotif, setEmailNotif] = useState(true);
  const [smsNotif, setSmsNotif] = useState(false);

  useEffect(() => {
    let alive = true;
    getSessionBusiness()
      .then((b) => alive && (setBusiness(b), setStatus("ready")))
      .catch((err) => {
        if (!alive) return;
        if (err?.response?.status === 401) {
          navigate("/signup", { replace: true });
          return;
        }
        setStatus("error");
      });
    return () => { alive = false; };
  }, [navigate]);

  const signOut = async () => {
    try { await logoutSession(); } catch (_) { /* best-effort */ }
    navigate("/", { replace: true });
  };

  const location = business
    ? [business.city, business.state].filter(Boolean).join(", ") || business.zipCode || ""
    : "";

  return (
    <AppShell showTabBar activeTab="settings" testId="settings-page">
      <div className="pt-6 sm:pt-10">
        <div className="text-xs uppercase tracking-widest font-medium mb-2" style={{ color: "var(--ta-secondary)" }}>
          Settings
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]" style={{ color: "var(--ta-primary)" }}>
          Your business.
        </h1>
        <p className="mt-3 text-lg leading-relaxed max-w-md" style={{ color: "var(--ta-text-secondary)" }}>
          A few simple choices. Change any of these anytime.
        </p>

        {status === "loading" && (
          <div className="flex items-center gap-2 text-sm mt-8" style={{ color: "var(--ta-text-secondary)" }} data-testid="settings-loading">
            <Loader2 size={16} className="animate-spin" /> Loading your business details…
          </div>
        )}

        {status === "error" && (
          <div className="mt-8" data-testid="settings-error">
            <div className="font-display text-2xl font-semibold" style={{ color: "var(--ta-primary)" }}>
              We couldn't load your business details.
            </div>
            <p className="mt-2 text-base" style={{ color: "var(--ta-text-secondary)" }}>
              Please try again shortly.
            </p>
          </div>
        )}

        {status === "ready" && business && (
          <>
            {/* Business profile */}
            <SectionTitle>Business details</SectionTitle>
            <Group>
              <Row
                icon={Store}
                title="Business name"
                value={business.businessName || "—"}
                onClick={() => toast.info("Editing your business details is coming soon.")}
                testId="settings-business-name"
              />
              <Row
                icon={User}
                title="Owner"
                value={business.ownerFirstName || "—"}
                onClick={() => toast.info("Editing your business details is coming soon.")}
                testId="settings-owner"
              />
              <Row
                icon={Store}
                title="Business type"
                value={business.businessType || "—"}
                onClick={() => toast.info("Editing your business details is coming soon.")}
                testId="settings-trade"
              />
              <Row
                icon={Store}
                title="Location"
                value={location || "—"}
                onClick={() => toast.info("Editing your business details is coming soon.")}
                testId="settings-location"
              />
            </Group>

            {/* Notifications */}
            <SectionTitle>Monday morning delivery</SectionTitle>
            <Group>
              <ToggleRow
                icon={Mail}
                title="Email me my brief"
                desc="7:00am on Mondays"
                checked={emailNotif}
                onChange={setEmailNotif}
                testId="settings-email-toggle"
              />
              <ToggleRow
                icon={MessageSquare}
                title="Also text me the highlights"
                desc="Coming soon"
                checked={smsNotif}
                onChange={setSmsNotif}
                disabled
                testId="settings-sms-toggle"
              />
            </Group>

            {/* Help */}
            <SectionTitle>Help</SectionTitle>
            <Group>
              <Row
                icon={HelpCircle}
                title="Talk to a real advisor"
                value="Weekdays 8–5 MT"
                onClick={() => toast("A human will reach out today.", { description: "We prefer text, but we'll call if you'd rather." })}
                testId="settings-advisor"
              />
              <Row
                icon={Bell}
                title="Notification history"
                value="Coming soon"
                onClick={() => toast.info("Notification history is coming soon.")}
                testId="settings-history"
                disabled
              />
            </Group>
          </>
        )}

        <button
          onClick={signOut}
          data-testid="settings-signout"
          className="mt-8 w-full inline-flex items-center justify-center gap-2 min-h-[56px] rounded-2xl text-base font-medium border-2"
          style={{ borderColor: "var(--ta-border)", color: "var(--ta-primary)", background: "#fff" }}
        >
          <LogOut size={18} /> Sign out
        </button>

        <p className="mt-6 text-center text-xs" style={{ color: "var(--ta-text-muted)" }}>
          Your Monday Brief · v1.0 preview
        </p>
      </div>
    </AppShell>
  );
}

const SectionTitle = ({ children }) => (
  <div className="text-xs uppercase tracking-widest font-semibold mt-8 mb-3 px-1" style={{ color: "var(--ta-secondary)" }}>
    {children}
  </div>
);

const Group = ({ children }) => (
  <div className="rounded-2xl bg-white border overflow-hidden" style={{ borderColor: "var(--ta-border)" }}>
    {children}
  </div>
);

const Row = ({ icon: Icon, title, value, onClick, testId, disabled }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    disabled={disabled}
    className="w-full flex items-center gap-4 px-5 py-5 text-left border-b active:bg-black/[0.02] transition-colors last:border-b-0 disabled:opacity-60"
    style={{ borderColor: "var(--ta-border)" }}
  >
    <div className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0" style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}>
      <Icon size={20} strokeWidth={2.2} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-medium text-base" style={{ color: "var(--ta-primary)" }}>{title}</div>
      <div className="text-sm truncate" style={{ color: "var(--ta-text-secondary)" }}>{value}</div>
    </div>
    <ChevronRight size={20} className="shrink-0" style={{ color: "var(--ta-text-muted)" }} />
  </button>
);

const ToggleRow = ({ icon: Icon, title, desc, checked, onChange, testId, disabled }) => (
  <div
    className="flex items-center gap-4 px-5 py-5 border-b last:border-b-0"
    style={{ borderColor: "var(--ta-border)", opacity: disabled ? 0.6 : 1 }}
  >
    <div className="w-10 h-10 rounded-xl inline-flex items-center justify-center shrink-0" style={{ background: "var(--ta-accent)", color: "var(--ta-primary)" }}>
      <Icon size={20} strokeWidth={2.2} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-medium text-base" style={{ color: "var(--ta-primary)" }}>{title}</div>
      <div className="text-sm" style={{ color: "var(--ta-text-secondary)" }}>{desc}</div>
    </div>
    <button
      onClick={() => !disabled && onChange(!checked)}
      data-testid={testId}
      disabled={disabled}
      role="switch"
      aria-checked={checked}
      className="w-14 h-8 rounded-full relative transition-colors shrink-0"
      style={{ background: checked ? "var(--ta-primary)" : "var(--ta-surface-alt)" }}
    >
      <span
        className="absolute top-1 w-6 h-6 rounded-full bg-white shadow-sm transition-all"
        style={{ left: checked ? "calc(100% - 28px)" : "4px" }}
      />
    </button>
  </div>
);
